import React, { useState, useMemo } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { useTranslation } from 'react-i18next';
import Canvas from './Canvas';
import Canvas3D from './Canvas3D';
import Inspector from './Inspector';
import SuggestionsPanel from './SuggestionsPanel';
import { GraphNode, GraphEdge, GraphData, NODE_STYLES } from './types';
import { can } from '../../lib/roles';
import { useAuthStore } from '../../store/authStore';
import { useAppStore } from '@/store/appStore';
import { AppScopeBanner } from '@/components/AppScopeBanner';
import { useTemplateSchemaGraph, useInstanceGraphData, useTwinRecordSources } from './api';
import { mergeGraphSources } from './graph3d/useGraph3DData';
import { TwinChainPanel } from './TwinChainPanel';
import {
  composeTwinChain,
  honestRelationshipTenant,
  twinChainToGraphData,
} from './twinChain';
import { getDefaultTenantId, isProductionHost } from '../../lib/cpClient';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import {
  Package, Wheat, CloudSun, Building2, Beef,
  ClipboardList, Users, FileImage, Bug, Wrench,
  Layers, Network, ChevronRight, ChevronDown, FolderOpen,
} from 'lucide-react';

type ViewMode = '2d' | '3d';
type DataSource = 'twins' | 'standards' | 'infrastructure' | 'combined';

function isSupersededEdge(e: GraphEdge): boolean {
  const propsStatus = String((e as any)?.props?.status || '').toLowerCase();
  const topStatus = String((e as any)?.status || '').toLowerCase();
  return propsStatus === 'superseded' || topStatus === 'superseded';
}

function isSupersededNode(n: GraphNode): boolean {
  const props = (n as any)?.props || {};
  const status = String(props.status || props.metadata?.status || (n as any).status || '').toLowerCase();
  const catalog = String(props.catalog_state || '').toLowerCase();
  return status === 'superseded' || catalog === 'superseded';
}

/** Default: bury corpses. Toggle reveals them with props preserved for badging. */
function applySupersededVisibility(data: GraphData, showSuperseded: boolean): GraphData {
  if (!data) return { nodes: [], edges: [] };
  if (showSuperseded) {
    return {
      nodes: data.nodes.map((n) =>
        isSupersededNode(n) ? { ...n, props: { ...(n.props || {}), _ui_superseded: true } } : n,
      ),
      edges: data.edges.map((e) =>
        isSupersededEdge(e)
          ? { ...e, props: { ...(e.props || {}), _ui_superseded: true } }
          : e,
      ),
    };
  }
  const edges = data.edges.filter((e) => !isSupersededEdge(e));
  const nodes = data.nodes.filter((n) => !isSupersededNode(n));
  return { nodes, edges };
}

const IKU_ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  Package, Wheat, CloudSun, Building2, Beef,
  ClipboardList, Users, FileImage, Bug, Wrench,
  Layers, Network,
};

function getIkuIcon(iconName?: string) {
  if (!iconName) return Layers;
  return IKU_ICON_MAP[iconName] || Layers;
}

function filterGraphForAllIkus(data: GraphData): GraphData {
  const ikuNodes = data.nodes.filter(n => n.kind === 'IKUCategory');
  const ikuIds = new Set(ikuNodes.map(n => n.id));
  const edges = data.edges.filter(e =>
    e.type === 'relationship_type_definition' && ikuIds.has(e.from) && ikuIds.has(e.to)
  );
  return { nodes: ikuNodes, edges };
}

function filterGraphForIku(data: GraphData, ikuId: string): GraphData {
  const selectedIkuNode = data.nodes.find(n => n.id === `iku:${ikuId}`);
  if (!selectedIkuNode) return { nodes: [], edges: [] };

  const templateNodes = data.nodes.filter(
    n => n.kind === 'Template' && n.props?.iku_category === ikuId
  );
  const templateIds = new Set(templateNodes.map(n => n.id));
  const selectedIkuFullId = `iku:${ikuId}`;

  const relevantEdges = data.edges.filter(e => {
    if (e.type === 'belongs_to_iku' && templateIds.has(e.from) && e.to === selectedIkuFullId) return true;
    if (templateIds.has(e.from) && e.from !== selectedIkuFullId) return true;
    if (e.type === 'relationship_type_definition' && (e.from === selectedIkuFullId || e.to === selectedIkuFullId)) return true;
    return false;
  });

  const connectedIkuIds = new Set<string>();
  relevantEdges.forEach(e => {
    if (e.to?.startsWith('iku:') && e.to !== selectedIkuFullId) connectedIkuIds.add(e.to);
    if (e.from?.startsWith('iku:') && e.from !== selectedIkuFullId) connectedIkuIds.add(e.from);
  });
  const connectedIkuNodes = data.nodes.filter(n => connectedIkuIds.has(n.id));

  return {
    nodes: [selectedIkuNode, ...templateNodes, ...connectedIkuNodes],
    edges: relevantEdges,
  };
}

function filterGraphForSubcategory(data: GraphData, ikuId: string, subcategory: string): GraphData {
  const selectedIkuNode = data.nodes.find(n => n.id === `iku:${ikuId}`);
  if (!selectedIkuNode) return { nodes: [], edges: [] };

  const templateNodes = data.nodes.filter(
    n => n.kind === 'Template' && n.props?.iku_category === ikuId && n.props?.subcategory === subcategory
  );
  if (templateNodes.length === 0) return { nodes: [selectedIkuNode], edges: [] };

  const templateIds = new Set(templateNodes.map(n => n.id));
  const selectedIkuFullId = `iku:${ikuId}`;

  const relevantEdges = data.edges.filter(e => {
    if (e.type === 'belongs_to_iku' && templateIds.has(e.from) && e.to === selectedIkuFullId) return true;
    if (templateIds.has(e.from)) return true;
    if (templateIds.has(e.to)) return true;
    return false;
  });

  const connectedNodeIds = new Set<string>();
  relevantEdges.forEach(e => {
    connectedNodeIds.add(e.from);
    connectedNodeIds.add(e.to);
  });
  templateIds.forEach(id => connectedNodeIds.delete(id));
  connectedNodeIds.delete(selectedIkuFullId);
  const connectedNodes = data.nodes.filter(n => connectedNodeIds.has(n.id));

  return {
    nodes: [selectedIkuNode, ...templateNodes, ...connectedNodes],
    edges: relevantEdges,
  };
}

function filterGraphForTemplate(data: GraphData, templateId: string): GraphData {
  const templateNode = data.nodes.find(n => n.id === templateId);
  if (!templateNode) return { nodes: [], edges: [] };

  const ikuId = templateNode.props?.iku_category;
  const ikuNodeId = ikuId ? `iku:${ikuId}` : null;
  const ikuNode = ikuNodeId ? data.nodes.find(n => n.id === ikuNodeId) : null;

  // All edges touching this template
  const relevantEdges = data.edges.filter(e =>
    e.from === templateId || e.to === templateId
  );

  // Collect all node IDs referenced by those edges
  const connectedNodeIds = new Set<string>();
  relevantEdges.forEach(e => {
    connectedNodeIds.add(e.from);
    connectedNodeIds.add(e.to);
  });
  connectedNodeIds.delete(templateId);

  // Always include the parent IKU even if no edge references it
  if (ikuNodeId) connectedNodeIds.add(ikuNodeId);

  const connectedNodes = data.nodes.filter(n => connectedNodeIds.has(n.id));

  // Make sure belongs_to_iku edge is included
  const belongsEdge = ikuNodeId
    ? data.edges.find(e => e.from === templateId && e.to === ikuNodeId && e.type === 'belongs_to_iku')
    : null;
  const allEdges = belongsEdge && !relevantEdges.some(e => e.id === belongsEdge.id)
    ? [...relevantEdges, belongsEdge]
    : relevantEdges;

  return {
    nodes: [templateNode, ...connectedNodes],
    edges: allEdges,
  };
}

function formatSubcategoryLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function RelationshipManager() {
  const { t } = useTranslation();
  const { token, selectedTenant } = useAuthStore();
  const { appName } = useAppStore();
  const appId = useSelectedAppId();
  const honestTenant = honestRelationshipTenant({
    selectedTenant,
    hostedTenant: getDefaultTenantId(),
    environment: isProductionHost() ? 'prod' : getDefaultTenantId(),
  });

  // ALL state declarations FIRST — before any useMemo/useCallback
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<GraphEdge | null>(null);
  const [selectedIku, setSelectedIku] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [expandedIkus, setExpandedIkus] = useState<Set<string>>(new Set());
  const [focusedTemplateId, setFocusedTemplateId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('2d');
  const [searchQuery, setSearchQuery] = useState('');
  const [dataSource, setDataSource] = useState<DataSource>('twins');
  /** UI truth F3 — superseded edges/templates buried by default */
  const [showSuperseded, setShowSuperseded] = useState(false);

  const { data: schemaData, isLoading, error } = useTemplateSchemaGraph();
  const needsInstanceGraph = dataSource === 'twins' || dataSource === 'infrastructure' || dataSource === 'combined';
  const { data: instanceGraphData, isLoading: instanceLoading, error: instanceError } = useInstanceGraphData(
    { scope: 'tenant' },
    needsInstanceGraph
  );
  const { data: twinRecords } = useTwinRecordSources();

  const twinChain = useMemo(() => composeTwinChain({
    appId,
    selectedTenant,
    hostedTenant: getDefaultTenantId(),
    environment: isProductionHost() ? 'prod' : getDefaultTenantId(),
    graph: instanceGraphData || { nodes: [], edges: [] },
    records: twinRecords || { entities: [], devices: [], products: [] },
  }), [appId, selectedTenant, instanceGraphData, twinRecords]);

  const twinGraphData = useMemo(() => twinChainToGraphData(twinChain), [twinChain]);

  // Resolve the active graph data based on selected data source
  const activeGraphData = useMemo(() => {
    if (dataSource === 'twins') return twinGraphData;
    if (dataSource === 'infrastructure') return instanceGraphData;
    if (dataSource === 'combined') {
      if (schemaData && instanceGraphData) return mergeGraphSources(schemaData, instanceGraphData);
      return instanceGraphData || schemaData;
    }
    return schemaData;
  }, [dataSource, schemaData, instanceGraphData, twinGraphData]);

  const canEdit = can('builder', token) || can('ops', token);

  const ikuNodes = useMemo(() => {
    if (!schemaData) return [];
    return schemaData.nodes
      .filter(n => n.kind === 'IKUCategory')
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [schemaData]);

  const subcategoryData = useMemo(() => {
    if (!schemaData) return new Map<string, Map<string, number>>();
    const map = new Map<string, Map<string, number>>();
    schemaData.nodes.forEach(n => {
      if (n.kind === 'Template' && n.props?.iku_category) {
        const ikuId = n.props.iku_category;
        if (!map.has(ikuId)) map.set(ikuId, new Map());
        const sub = n.props.subcategory || '_uncategorized';
        const subMap = map.get(ikuId)!;
        subMap.set(sub, (subMap.get(sub) || 0) + 1);
      }
    });
    return map;
  }, [schemaData]);

  const templatesForSelection = useMemo(() => {
    if (!schemaData || !selectedIku) return [];
    return schemaData.nodes.filter(n => {
      if (n.kind !== 'Template' || n.props?.iku_category !== selectedIku) return false;
      if (selectedSubcategory) {
        const sub = n.props?.subcategory || '_uncategorized';
        return sub === selectedSubcategory;
      }
      return true;
    });
  }, [schemaData, selectedIku, selectedSubcategory]);

  const templateCountByIku = useMemo(() => {
    if (!schemaData) return new Map<string, number>();
    const counts = new Map<string, number>();
    schemaData.nodes.forEach(n => {
      if (n.kind === 'Template' && n.props?.iku_category) {
        counts.set(n.props.iku_category, (counts.get(n.props.iku_category) || 0) + 1);
      }
    });
    return counts;
  }, [schemaData]);

  const filteredGraph = useMemo<GraphData>(() => {
    const source = activeGraphData;
    if (!source) return { nodes: [], edges: [] };
    let next: GraphData;
    // Twins / infra use the composed instance graph. IKU filters are standards-only.
    if (dataSource !== 'standards') {
      next = source;
    } else if (focusedTemplateId) {
      next = filterGraphForTemplate(source, focusedTemplateId);
    } else if (!selectedIku) {
      next = filterGraphForAllIkus(source);
    } else if (selectedSubcategory) {
      next = filterGraphForSubcategory(source, selectedIku, selectedSubcategory);
    } else {
      next = filterGraphForIku(source, selectedIku);
    }
    return applySupersededVisibility(next, showSuperseded);
  }, [activeGraphData, dataSource, selectedIku, selectedSubcategory, focusedTemplateId, showSuperseded]);

  const layoutMode = useMemo(() => {
    if (dataSource === 'twins') return 'twin-chain' as const;
    if (focusedTemplateId) return 'iku-focus' as const;
    if (!selectedIku) return 'all-ikus' as const;
    return 'iku-focus' as const;
  }, [dataSource, selectedIku, focusedTemplateId]);

  const clearSelection = () => {
    setSelectedNode(null);
    setSelectedEdge(null);
  };

  const resetToAllIkus = () => {
    setSelectedIku(null);
    setSelectedSubcategory(null);
    setFocusedTemplateId(null);
    clearSelection();
  };

  const toggleIkuExpanded = (ikuId: string) => {
    setExpandedIkus(prev => {
      const next = new Set(prev);
      if (next.has(ikuId)) next.delete(ikuId);
      else next.add(ikuId);
      return next;
    });
  };

  const handleIkuClick = (ikuId: string) => {
    setFocusedTemplateId(null);
    if (selectedIku === ikuId && !selectedSubcategory) {
      setSelectedIku(null);
      setSelectedSubcategory(null);
    } else {
      setSelectedIku(ikuId);
      setSelectedSubcategory(null);
      if (!expandedIkus.has(ikuId)) toggleIkuExpanded(ikuId);
    }
    clearSelection();
  };

  const handleSubcategoryClick = (ikuId: string, subcategory: string) => {
    setFocusedTemplateId(null);
    setSelectedIku(ikuId);
    setSelectedSubcategory(selectedSubcategory === subcategory ? null : subcategory);
    clearSelection();
  };

  const handleTemplateClick = (template: GraphNode) => {
    setFocusedTemplateId(template.id);
    setSelectedNode(template);
    setSelectedEdge(null);
  };

  const handleNodeSelect = (node: GraphNode) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  };

  const handleEdgeSelect = (edge: GraphEdge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  };

  const totalTemplates = activeGraphData?.nodes.filter(n => n.kind === 'Template').length || 0;
  const totalRelTypes = activeGraphData?.edges.filter(e => e.type === 'relationship_type_definition').length || 0;

  const breadcrumb = useMemo(() => {
    const parts: string[] = [];
    if (selectedIku) {
      const ikuNode = ikuNodes.find(n => n.props?.entity_id === selectedIku);
      parts.push(ikuNode?.label || selectedIku);
    }
    if (selectedSubcategory) {
      parts.push(formatSubcategoryLabel(selectedSubcategory));
    }
    if (focusedTemplateId && selectedNode) {
      parts.push(selectedNode.label);
    }
    return parts;
  }, [selectedIku, selectedSubcategory, focusedTemplateId, selectedNode, ikuNodes]);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="px-6 pt-3 flex-shrink-0">
        <AppScopeBanner />
      </div>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {t('relationships.title', 'Relationship Manager')}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {dataSource === 'twins'
                ? `Product → Legal Entity → Asset → Device${appName ? ` · ${appName}` : ''}${honestTenant ? ` · tenant ${honestTenant}` : ' · tenant unknown (not stamped as dev)'}`
                : breadcrumb.length > 0
                ? `${breadcrumb.join(' / ')} — ${focusedTemplateId ? `${filteredGraph.edges.length} connection(s)` : `${templatesForSelection.length} template(s)`}`
                : `${ikuNodes.length} IKU categories — ${totalTemplates} templates — ${totalRelTypes} relationship types`
              }
            </p>
            <p className="text-[11px] text-gray-400 mt-1">
              {dataSource === 'twins'
                ? 'Digital twin chain from instance relationships. A missing PIM product stays empty — it is not invented.'
                : 'Standards relationship-type / template graph — a different store from PIM instance edges. Zero here is not a PIM join.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* 3D Search (visible in 3D mode) */}
            {viewMode === '3d' && (
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search nodes..."
                className="px-3 py-1.5 border border-gray-300 rounded-md text-xs w-40 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            )}

            <button
              type="button"
              onClick={() => setShowSuperseded((v) => !v)}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium border ${
                showSuperseded
                  ? 'bg-amber-50 text-amber-900 border-amber-300'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
              title="Superseded edges/templates are hidden by default"
            >
              {showSuperseded ? 'Superseded: shown' : 'Show superseded'}
            </button>

            {/* Data Source Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs font-medium">
              {(['twins', 'standards', 'infrastructure', 'combined'] as DataSource[]).map(src => (
                <button
                  key={src}
                  onClick={() => setDataSource(src)}
                  className={`px-2.5 py-1.5 rounded-md transition-all capitalize ${
                    dataSource === src ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title={
                    src === 'twins' ? 'Product → Legal Entity → Asset → Device'
                    : src === 'standards' ? 'Templates, IKUs, relationship types'
                    : src === 'infrastructure' ? 'Devices, agents, services, topics'
                    : 'Both sources merged (deduped by entity key)'
                  }
                >
                  {src === 'twins' ? 'Twins' : src === 'standards' ? 'Standards' : src === 'infrastructure' ? 'Infra' : 'Combined'}
                  {src !== 'standards' && instanceLoading && src === dataSource && (
                    <span className="ml-1 opacity-60">…</span>
                  )}
                </button>
              ))}
            </div>

            {/* 2D / 3D View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs font-medium">
              <button
                onClick={() => setViewMode('2d')}
                className={`px-3 py-1.5 rounded-md transition-all ${
                  viewMode === '2d' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                2D
              </button>
              <button
                onClick={() => setViewMode('3d')}
                className={`px-3 py-1.5 rounded-md transition-all ${
                  viewMode === '3d' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                3D
              </button>
            </div>

            {appName && (
              <div className="px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                {appName}
              </div>
            )}
            {(selectedIku || focusedTemplateId) && viewMode === '2d' && (
              <button
                onClick={resetToAllIkus}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-xs font-medium"
              >
                Back to All IKUs
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 3-column layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-72 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 overflow-hidden">
          {dataSource === 'twins' ? (
            <div className="px-4 py-3 text-left text-sm font-semibold border-b border-gray-100 flex items-center gap-2 bg-indigo-50 text-indigo-700">
              <Network size={16} />
              Twin chain
              <span className="ml-auto text-xs font-normal text-gray-400">4 roles</span>
            </div>
          ) : (
          <button
            onClick={resetToAllIkus}
            className={`w-full px-4 py-3 text-left text-sm font-semibold border-b border-gray-100 flex items-center gap-2 transition-colors ${
              !selectedIku && !focusedTemplateId ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Network size={16} />
            All IKU Categories
            <span className="ml-auto text-xs font-normal text-gray-400">{ikuNodes.length}</span>
          </button>
          )}

          <div className="flex-1 overflow-y-auto">
            {dataSource === 'twins' && (
              <>
                {(instanceLoading && needsInstanceGraph) && (
                  <div className="p-4 text-sm text-gray-400">Loading twin relationships...</div>
                )}
                {instanceError && (
                  <div className="p-4 text-sm text-red-500">Failed to load instance relationships — honest empty, not a dump.</div>
                )}
                <TwinChainPanel chain={twinChain} appName={appName} />
              </>
            )}
            {dataSource !== 'twins' && isLoading && <div className="p-4 text-sm text-gray-400">Loading standards schema...</div>}
            {(instanceLoading && needsInstanceGraph && dataSource !== 'twins') && (
              <div className="p-4 text-sm text-gray-400">Loading instance relationships...</div>
            )}
            {dataSource !== 'twins' && error && <div className="p-4 text-sm text-red-500">Failed to load schema</div>}
            {dataSource !== 'twins' && ikuNodes.map(iku => {
              const ikuId = iku.props?.entity_id || iku.id.replace('iku:', '');
              const isActive = selectedIku === ikuId;
              const isExpanded = expandedIkus.has(ikuId);
              const count = templateCountByIku.get(ikuId) || 0;
              const color = iku.props?.color || '#6B7280';
              const IconComp = getIkuIcon(iku.props?.icon);
              const subcats = iku.props?.subcategories || {};
              const subcatKeys = Object.keys(subcats).sort();
              const hasSubcats = subcatKeys.length > 0;
              const subCountMap = subcategoryData.get(ikuId);

              return (
                <div key={iku.id}>
                  <div className="flex items-center">
                    {hasSubcats ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleIkuExpanded(ikuId); }}
                        className="w-6 h-6 flex items-center justify-center ml-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                      >
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    ) : (
                      <div className="w-6 ml-1 flex-shrink-0" />
                    )}
                    <button
                      onClick={() => handleIkuClick(ikuId)}
                      className={`flex-1 px-2 py-2.5 text-left text-sm flex items-center gap-2 transition-colors border-l-[3px] ${
                        isActive && !selectedSubcategory && !focusedTemplateId
                          ? 'bg-gray-100 font-medium'
                          : isActive
                          ? 'bg-gray-50'
                          : 'hover:bg-gray-50 border-transparent'
                      }`}
                      style={(isActive && !selectedSubcategory && !focusedTemplateId) ? { borderLeftColor: color } : isActive ? { borderLeftColor: `${color}60` } : undefined}
                    >
                      <div
                        className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}15`, color }}
                      >
                        <IconComp size={13} />
                      </div>
                      <span className="truncate flex-1 text-[13px]">{iku.label}</span>
                      {count > 0 && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{count}</span>
                      )}
                    </button>
                  </div>

                  {isExpanded && hasSubcats && (
                    <div className="bg-gray-50/50">
                      {subcatKeys.map(subKey => {
                        const subLabel = subcats[subKey]?.name || formatSubcategoryLabel(subKey);
                        const subCount = subCountMap?.get(subKey) || 0;
                        const isSubActive = selectedIku === ikuId && selectedSubcategory === subKey && !focusedTemplateId;

                        return (
                          <button
                            key={subKey}
                            onClick={() => handleSubcategoryClick(ikuId, subKey)}
                            className={`w-full pl-12 pr-3 py-1.5 text-left text-xs flex items-center gap-2 transition-colors ${
                              isSubActive
                                ? 'bg-indigo-50 text-indigo-700 font-medium'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                          >
                            <FolderOpen size={11} className="flex-shrink-0 opacity-60" />
                            <span className="truncate flex-1">{subLabel}</span>
                            {subCount > 0 && (
                              <span className="text-[10px] text-gray-400">{subCount}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {isActive && templatesForSelection.length > 0 && (
                    <div className="bg-gray-50 border-t border-b border-gray-100">
                      {templatesForSelection.map(tmpl => {
                        const relCount = Object.values(tmpl.props?.relationships || {}).flat().length;
                        const isFocused = focusedTemplateId === tmpl.id;
                        return (
                          <button
                            key={tmpl.id}
                            onClick={() => handleTemplateClick(tmpl)}
                            className={`w-full pl-14 pr-3 py-1.5 text-left text-[11px] flex items-center gap-2 transition-colors ${
                              isFocused
                                ? 'bg-indigo-50 text-indigo-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isFocused ? 'bg-indigo-500' : 'bg-amber-400'}`} />
                            <span className="truncate flex-1">{tmpl.label}</span>
                            <span className="text-[9px] text-gray-400 whitespace-nowrap">
                              {tmpl.props?.attribute_count || 0}a · {relCount}r
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {isActive && templatesForSelection.length === 0 && (
                    <div className="bg-gray-50 border-t border-b border-gray-100 px-4 py-2">
                      <p className="text-[11px] text-gray-400 italic pl-10">No templates</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 flex-shrink-0">
            <div className="text-[10px] text-gray-400 space-y-0.5">
              {dataSource === 'twins' ? (
                <>
                  <div>{twinChain.nodes.filter((n) => n.present).length} of 4 twin roles linked</div>
                  <div>{twinChain.missing.length} honest-empty slot(s)</div>
                </>
              ) : (
                <>
                  <div>{totalTemplates} templates across {ikuNodes.length} IKUs</div>
                  <div>{totalRelTypes} relationship type definitions</div>
                </>
              )}
              <div className="text-[9px]">Graph: {filteredGraph.nodes.length} nodes, {filteredGraph.edges.length} edges</div>
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative overflow-hidden">
          {viewMode === '2d' ? (
            <>
              <ReactFlowProvider>
                <Canvas
                  data={filteredGraph}
                  layoutMode={layoutMode}
                  selectedIku={selectedIku}
                  onNodeSelect={handleNodeSelect}
                  onEdgeSelect={handleEdgeSelect}
                />
              </ReactFlowProvider>
              <SuggestionsPanel />
            </>
          ) : (
            <Canvas3D
              data={activeGraphData}
              onNodeSelect={handleNodeSelect}
              onEdgeSelect={handleEdgeSelect}
              searchQuery={searchQuery}
            />
          )}
        </div>

        {/* Inspector */}
        {(selectedNode || selectedEdge) && (
          <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0">
            <Inspector
              node={selectedNode}
              edge={selectedEdge}
              graphData={activeGraphData || undefined}
              onClose={clearSelection}
              canEdit={canEdit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
